import {
  Children,
  cloneElement,
  createContext,
  useContext,
  useState,
} from 'react';
import classNames from 'classnames';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { _noop, arrify } from '@client/new/helpers/general';

type AccordionContextProps = {
  setActiveIndex: (index: number) => void;
  getIsActive: (index: number) => boolean;
};

const AccordionContext = createContext<AccordionContextProps>({
  setActiveIndex: _noop,
  getIsActive: () => false,
});

export type AccordionProps = {
  allowMultiple?: boolean;
  children: Array<JSX.Element> | JSX.Element;
  initialActiveIndexes?: number[] | number;
};

export const Accordion = (p: AccordionProps) => {
  const [activeIndexes, setActiveIndex] = useState<Set<number>>(
    () =>
      new Set(
        p.initialActiveIndexes === undefined
          ? []
          : arrify(p.initialActiveIndexes),
      ),
  );

  return (
    <AccordionContext.Provider
      value={{
        getIsActive: (index) => activeIndexes.has(index),
        setActiveIndex: (index) =>
          setActiveIndex((v) => {
            if (v.has(index)) {
              v.delete(index);
            } else {
              if (!p.allowMultiple) v.clear();
              v.add(index);
            }

            return new Set(v);
          }),
      }}
    >
      <div className="flex flex-col gap-3">
        {Children.map(p.children, (child, index) =>
          cloneElement(child, { index }),
        )}
      </div>
    </AccordionContext.Provider>
  );
};

export const AccordionItem = ({
  index = 0,
  children,
  title,
  iconWidth = 12,
}: {
  index?: number;
  children: React.ReactNode;
  title: string | React.ReactNode;
  iconWidth?: number;
}) => {
  const [ref] = useAutoAnimate({ duration: 150 });
  const { getIsActive, setActiveIndex } = useContext(AccordionContext);
  const isActive = getIsActive(index);

  return (
    <div ref={ref}>
      <div
        className="flex justify-between pb-2 text-lg font-semibold cursor-pointer items-center gap-2"
        onClick={() => setActiveIndex(index)}
      >
        {title}
        {iconWidth === 0 && (
          <FontAwesomeIcon
            width={iconWidth}
            strokeWidth={2}
            icon={faChevronDown}
            color="#404040"
            className={classNames('font-semibold transition-transform', {
              'rotate-180': isActive,
            })}
          />
        )}
      </div>

      {isActive && children}
    </div>
  );
};
